(() => {
  const tag = 'pm-pdp-original-motion';
  if (customElements.get(tag)) return;
  customElements.define(tag, class extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video');
      if (!this.video) return;
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.visible = false;
      // Set once when the browser autoplay policy rejects play(): the clip
      // stays on its stable poster with no retry loop and no play button.
      this.blocked = false;
      this.pending = false;
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.motion.addEventListener('change', () => this.sync(true), options);
      document.addEventListener('visibilitychange', () => this.sync(), options);
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(([entry]) => {
          this.visible = entry.isIntersecting && entry.intersectionRatio >= 0.25;
          this.sync();
        }, { threshold: [0, 0.25] });
        this.observer.observe(this);
      } else {
        const check = () => {
          const box = this.getBoundingClientRect();
          this.visible = box.bottom > 0 && box.top < innerHeight && box.right > 0 && box.left < innerWidth;
          this.sync();
        };
        window.addEventListener('scroll', check, { ...options, passive: true });
        window.addEventListener('resize', check, options);
        check();
      }
    }
    // Single reconciler: the decorative loop plays whenever the section is
    // sufficiently visible, the page is shown and reduced motion is off;
    // otherwise it pauses. Every state transition re-runs it, so leaving and
    // re-entering the viewport resumes playback (no one-shot latch).
    sync(motionChanged) {
      const video = this.video;
      if (!video || this.abort.signal.aborted) return;
      if (this.motion.matches) {
        if (!video.paused) video.pause();
        if (motionChanged && video.readyState > 0) {
          // load() with preload="none" discards the buffered frame so the
          // stable poster shows while the preference stays on reduce.
          try { video.load(); } catch (error) { /* poster restore is best-effort */ }
        }
        return;
      }
      if (!this.visible || document.hidden) {
        if (!video.paused) video.pause();
        return;
      }
      if (this.blocked || this.pending || !video.paused) return;
      // Sound policy: always muted. The clip is decorative; muting before
      // every attempt both matches intent and keeps autoplay policy-legal.
      video.muted = true;
      video.defaultMuted = true;
      this.pending = true;
      Promise.resolve(video.play()).catch((error) => {
        // AbortError means our own pause/load interrupted the attempt (fast
        // scroll past); that may retry on the next entry. Anything else is a
        // genuine policy rejection: keep the stable poster, never retry.
        if (!error || error.name !== 'AbortError') {
          this.blocked = true;
          video.pause();
          // A later policy rejection can follow earlier playback. Discard
          // that buffered frame as well, leaving the same stable poster.
          if (video.readyState > 0) {
            try { video.load(); } catch (loadError) { /* retain stable sizing */ }
          }
        }
      }).then(() => {
        this.pending = false;
        // Conditions may have changed while play() settled.
        if (this.abort.signal.aborted) return;
        if ((!this.visible || document.hidden || this.motion.matches) && !video.paused) video.pause();
      });
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.observer?.disconnect();
      this.pending = false;
      this.video?.pause();
    }
  });
})();
